import { useEffect, useRef } from 'react';
import { usePlayerStore } from '@/store/playerStore';

/**
 * Arkaplanda şarkı çalma işlemini yöneten bileşen.
 * UI görüntülemez, sadece ses çalma mantığını ve klavye kısayollarını yönetir.
 */
export function AudioPlayer() {
  const { 
    currentSong, 
    isPlaying, 
    volume, 
    setDuration,
    setProgress,
    progress,
    skipBackward,
    skipToEnd
  } = usePlayerStore();
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  // Ses dosyasını yükle
  useEffect(() => {
    if (!currentSong?.cdnUrl) return;
    
    if (audioRef.current) {
      audioRef.current.src = currentSong.cdnUrl;
      audioRef.current.preload = "auto"; // Ön yükleme yapılsın
      audioRef.current.load();
      
      // Play işlemini hızlandırmak için
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(err => console.error('Şarkı çalma hatası:', err));
      }
    }
  }, [currentSong]);
  
  // Oynatma durumunu kontrol et
  useEffect(() => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(err => console.error('Şarkı çalma hatası:', err));
      }
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying]);
  
  // Ses seviyesini kontrol et
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);
  
  // İlerleme değiştiğinde seek etme
  useEffect(() => {
    if (!audioRef.current || !currentSong) return;
    
    // İlerleme çubuğundan manuel olarak yapılan değişiklikler
    if (Math.abs(audioRef.current.currentTime - progress) > 1) {
      audioRef.current.currentTime = progress;
    }
  }, [progress, currentSong]);
  
  // Zaman güncellemelerini dinle
  useEffect(() => {
    if (!audioRef.current) return;
    
    const handleTimeUpdate = () => {
      if (audioRef.current) {
        setProgress(audioRef.current.currentTime);
      }
    };
    
    const handleLoadedMetadata = () => {
      if (audioRef.current) {
        setDuration(audioRef.current.duration);
      }
    };
    
    audioRef.current.addEventListener('timeupdate', handleTimeUpdate);
    audioRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
    
    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('timeupdate', handleTimeUpdate);
        audioRef.current.removeEventListener('loadedmetadata', handleLoadedMetadata);
      }
    };
  }, [setProgress, setDuration]);
  
  // Klavye kısayollarını dinle (basit tutuyoruz)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!currentSong) return;
      
      switch (e.key) {
        case 'F7': 
          e.preventDefault();
          skipBackward(10);
          break;
        case 'F9':
          e.preventDefault();
          if (e.shiftKey) {
            // Shift+F9: Tam ekran modu
            console.log('Tam ekran modu istendi');
            // Bellek kayıtlarına göre tam ekran modu uygulanmalı
          } else {
            // F9: Son 20 saniyeye atla
            skipToEnd(20);
          }
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentSong, skipBackward, skipToEnd]);
  
  return (
    <audio ref={audioRef} preload="auto" className="hidden" />
  );
}
